import { useEffect, useMemo, useState } from "react";
import { Option } from "../../common/commonSelect";
import webservice_url from "../../webservice/Webservice_url";
import useApi from "../useApi";

interface DataTypes {
  program_id: string;
  program_code: string;
  program_name: string;
}

// ========= Admin PORTAL ===========

// Programs by multiple project IDs and location IDs
export function usePrograms(
  projectIds: string[],
  locationIds: string[],
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>("", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // Convert API data into dropdown options
  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.program_name ?? "",
      value: d?.program_id ?? "",
    }));
  }, [dataList]);

  // Fetch programs for selected projects and locations
  const fetchData = async () => {
    if (
      !Array.isArray(projectIds) ||
      projectIds.length === 0 ||
      !Array.isArray(locationIds) ||
      locationIds.length === 0
    ) {
      setDataList([]);
      return;
    }

    try {
      const payload = {
        projectIds: projectIds,
        locationNames: locationIds,
      };

      // Single POST request
      const res = await callApi(
        payload,
        webservice_url.get_programs,
      );

      // API response is { data: [...] }
      const data: DataTypes[] = Array.isArray(res?.data)
        ? res.data
        : [];

      // Remove duplicate programs using program_id
      const uniqueData: DataTypes[] = Array.from(
        new Map<string, DataTypes>(
          data.map((item) => [
            item.program_id,
            item,
          ]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      console.error("Error fetching programs:", err);
      setDataList([]);
    }
  };

  // Fetch whenever selected projects or locations change
  const projectKey = JSON.stringify(projectIds ?? []);
  const locationKey = JSON.stringify(locationIds ?? []);

  useEffect(() => {
    if (
      !Array.isArray(projectIds) ||
      projectIds.length === 0 ||
      !Array.isArray(locationIds) ||
      locationIds.length === 0
    ) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, locationKey]);

  return {
    dataList,
    options,
    loading,
    error,
    fetchData,
  };
}

// ========= END ADMIN PORTAL ===========


// Programs by managerId
export function useProgramsByManagerId(
  managerId: string,
  projectId: string,
  locationName: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading } = useApi<any>(
    webservice_url.get_programs_by_managerId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.program_name ?? "",
      value: d?.program_id ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await callApi({
        manager_id: managerId ?? "",
        project_id: projectId ?? "",
        location_name: locationName ?? "",
      });

      setDataList(
        Array.isArray(res)
          ? res
          : (res?.data ?? []),
      );
    } catch (err: any) {
      console.error(err);
      setDataList([]);
    }
  };

  useEffect(() => {
    if (!managerId || !projectId || !locationName) {
      return;
    }

    fetchData();
  }, [managerId, projectId, locationName]);

  return {
    dataList,
    options,
    loading,
  };
}


// ========= VERIFIER PORTAL ===========

export function usePrograms_Verifier(
  projectId: string,
  location: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  const { callApi, loading } = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const options = useMemo<Option[]>(() => {
    return dataList.map((d) => ({
      label: d.program_name,
      value: d.program_id,
    }));
  }, [dataList]);

  const fetchData = async () => {
    if (!projectId || !location) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi(
        undefined,
        `${webservice_url.fetchProgram}?project_id=${encodeURIComponent(
          projectId,
        )}&location_name=${encodeURIComponent(location)}`,
      );

      setDataList(
        Array.isArray(res)
          ? res
          : (res?.data ?? []),
      );
    } catch (err) {
      setDataList([]);
      console.error(err);
    }
  };

  return {
    dataList,
    options,
    loading,
    fetchData,
  };
}

// ========= END VERIFIER PORTAL ===========


// Used for multiple projects and locations
export function useMultiPrograms(
  projectId: string[],
  location: string[],
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  const { callApi, loading, error } = useApi<any>(
    webservice_url.getProgramByMultiLocation,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.program_code ?? "",
      value: d?.program_id ?? "",
    }));
  }, [dataList]);

  const fetchData = async () => {
    if (
      !Array.isArray(projectId) ||
      projectId.length === 0 ||
      !Array.isArray(location) ||
      location.length === 0
    ) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi({
        projectIds: projectId,
        locationNames: location,
      });

      const responseData: DataTypes[] = Array.isArray(res)
        ? res
        : (res?.data ?? []);

      // Remove duplicate programs
      const uniqueData = Array.from(
        new Map(
          responseData.map((item) => [item.program_id, item]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      setDataList([]);
      console.error(err);
    }
  };

  const projectKey = JSON.stringify(projectId ?? []);
  const locationKey = JSON.stringify(location ?? []);

  useEffect(() => {
    if (
      !Array.isArray(projectId) ||
      projectId.length === 0 ||
      !Array.isArray(location) ||
      location.length === 0
    ) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, locationKey]);

  return {
    dataList,
    options,
    loading,
    error,
    fetchData,
  };
}